"use client";

import { ArrowDown, Check, ChevronRight, Play, Sparkles } from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import type { PlatformId } from "@/types/platforms";

const HERO_PLATFORMS: PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "linkedin",
  "x",
];

export function Hero() {
  const { t } = useJsonTranslation("Hero");

  return (
    <div
      data-home-block="hero"
      className="relative flex min-h-[62vh] flex-col items-center justify-center overflow-hidden px-1 pt-24 pb-4 sm:pt-28"
    >
      {/* Luxury layer: aurora orb + hero-grid */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div
          className="animate-aurora absolute top-4 left-1/2 h-[560px] w-[840px] max-w-[100vw] -translate-x-1/2 rounded-full blur-[150px]"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(52,211,153,0.24) 0%, rgba(255,122,24,0.16) 45%, transparent 72%)",
          }}
          aria-hidden="true"
        />
        <div
          className="hero-grid absolute inset-0 opacity-70"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-4xl text-center">
        {/* Live badge — single clean signal */}
        <div className="glass mb-7 inline-flex items-center gap-2.5 rounded-full px-5 py-2.5 text-sm shadow-[0_10px_32px_rgba(16,185,129,0.08)]">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="bg-leaf-400 absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" />
            <span className="bg-leaf-500 relative inline-flex h-2 w-2 rounded-full" />
          </span>
          <span className="text-foreground/70 font-medium tracking-wide">
            {t("home.hero.badge")}
          </span>
        </div>

        {/* Main headline */}
        <h1 className="font-home-display mb-5 text-4xl leading-[0.96] font-semibold tracking-normal text-balance sm:text-6xl lg:text-7xl xl:text-[5rem]">
          <span className="text-foreground block">
            {t("home.hero.headline1")}
          </span>
          <span className="text-gradient-primary mt-1 block">
            {t("home.hero.headline2")}
          </span>
        </h1>

        {/* Subheadline — one clear sentence */}
        <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-pretty sm:text-xl">
          {t("home.hero.subheadline")}
        </p>

        {/* 6-platform preview strip — concrete anchor for "6 platforms" claim */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <span className="text-muted-foreground/60 mr-1 text-[11px] font-semibold tracking-[0.22em] uppercase sm:text-xs">
            {t("home.hero.optimizedFor")}
          </span>
          {HERO_PLATFORMS.map((p) => (
            <div
              key={p}
              className="glass glass-hover hover:border-leaf-500/40 group flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 hover:-translate-y-0.5"
              title={p}
              aria-label={p}
            >
              <PlatformLogo
                platform={p}
                size={22}
                className="opacity-85 transition-opacity duration-300 group-hover:opacity-100"
              />
            </div>
          ))}
        </div>

        {/* CTA row — single primary + secondary scroll link */}
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-5">
          <Link href="/signup">
            <Button size="xl" className="group shadow-glow-lg">
              <Play className="h-5 w-5 fill-current" aria-hidden="true" />
              {t("home.hero.cta")}
              <ChevronRight
                className="h-4 w-4 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Button>
          </Link>
          <a
            href="#live-demo"
            className="group text-foreground/70 hover:text-foreground focus-visible:ring-ring inline-flex min-h-[44px] items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none"
          >
            <span className="underline-offset-4 group-hover:underline">
              {t("home.hero.ctaSecondary")}
            </span>
            <ArrowDown
              className="h-4 w-4 animate-bounce transition-transform"
              aria-hidden="true"
            />
          </a>
        </div>

        {/* Trust row — reduced to 3 signals, subtle, one line */}
        <div className="text-muted-foreground/70 mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs tracking-wide sm:text-sm">
          <span className="inline-flex items-center gap-1.5">
            <Check
              className="text-leaf-500 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            {t("home.hero.trustSignal1")}
          </span>
          <span
            className="text-foreground/20 hidden sm:inline"
            aria-hidden="true"
          >
            ·
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Check
              className="text-leaf-500 h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            {t("home.hero.trustSignal2")}
          </span>
          <span
            className="text-foreground/20 hidden sm:inline"
            aria-hidden="true"
          >
            ·
          </span>
          <span className="text-leaf-600 dark:text-leaf-400 inline-flex items-center gap-1.5 font-medium">
            <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t("home.hero.moneyBack")}
          </span>
        </div>
      </div>
    </div>
  );
}
